import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { FINALIDADES } from "@/components/EtapaOperacao";

/* ====== IMPRESSÃO: Ticket de Pesagem da Balança Rodoviária (mesmo padrão do ticket do Agendamento de Doca) ====== */

const fmtDT = (s) => (s ? new Date(s).toLocaleString("pt-BR") : "—");
const kg = (v) => (v != null ? `${Number(v).toLocaleString("pt-BR")} kg` : "—");
const ROTULOS = {
  DESCARGA: ["Peso Inicial (Cheio)", "Peso Final (Vazio)", "Peso Líquido Recebido"],
  CARGA: ["Peso Inicial (Vazio)", "Peso Final (Cheio)", "Peso Líquido Expedido"],
  MISTA: ["Peso na Entrada", "Peso na Saída", "Diferença Líquida"],
  OUTROS: ["Peso na Entrada", "Peso na Saída", "Diferença Líquida"],
};
const TIPO_TXT = { CARGA: "CARGA (sai cheio)", DESCARGA: "DESCARGA (sai vazio)", OUTROS: "OUTROS / TRANSFERÊNCIA", MISTA: "MISTA" };
const ETAPA_TXT = { CONCLUIDO: "FEITO", EM_ANDAMENTO: "EM ANDAMENTO", AGUARDANDO: "AGUARDANDO", CANCELADO: "CANCELADO", NAO_REALIZADO: "NÃO REALIZADO" };

export default function PrintTicketBalanca() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    api.get(`/balanca/pesagens/${id}`).then(({ data }) => {
      setT(data);
      if (new URLSearchParams(window.location.search).get("auto") !== "0") setTimeout(() => window.print(), 600);
    }).catch(() => setErro("Ticket não encontrado"));
  }, [id]);

  if (erro) return <div className="p-10 font-mono text-sm">{erro}</div>;
  if (!t) return <div className="p-10 font-mono text-sm">Carregando ticket...</div>;
  const [r1, r2, r3] = ROTULOS[t.tipo_operacao] || ROTULOS.MISTA;
  const b = t.base_info || {};
  const fins = t.finalidades || [];

  return (
    <div className="bg-white text-black min-h-screen p-8 font-sans text-[12px]" data-testid="print-ticket-balanca">
      <style>{`@media print { .no-print { display: none } } body { background: #fff }`}</style>
      <button onClick={() => window.print()} className="no-print mb-4 border border-black px-4 py-2 text-xs font-bold uppercase" data-testid="ticket-imprimir">Imprimir</button>

      {/* Cabeçalho */}
      <div className="flex justify-between items-start border-b-2 border-black pb-3">
        <div>
          <div className="text-xl font-black">CHEM·WMS — TICKET DE PESAGEM</div>
          <div className="text-[11px]">Base {b.code || t.base} · Documento gerado em {new Date().toLocaleString("pt-BR")}</div>
          {(b.razao_social || b.cnpj || b.endereco) && (
            <div className="text-[11px] mt-0.5">{[b.razao_social, b.cnpj && `CNPJ ${b.cnpj}`, b.endereco].filter(Boolean).join(" · ")}</div>
          )}
        </div>
        <div className="text-right">
          <div className="font-mono text-lg font-black" data-testid="ticket-numero">{t.ticket_str}</div>
          <div className="text-[11px]">Balança rodoviária · {t.status === "FINALIZADO" ? "FINALIZADO" : t.status === "ABERTO" ? "AGUARDANDO 2ª PESAGEM" : t.status}</div>
          {t.avulsa && <div className="text-[10px] font-bold uppercase border border-black inline-block px-1 mt-0.5" data-testid="ticket-avulsa-tag">Pesagem avulsa — sem entrada na Portaria</div>}
        </div>
      </div>

      {/* Dados do transporte */}
      <table className="w-full mt-3 text-[11px]">
        <tbody>
          <tr>
            <td className="py-1"><b>Transportadora:</b> {t.empresa || "—"}</td>
            <td><b>Motorista:</b> {t.nome}</td>
            <td><b>CPF:</b> {t.cpf || "—"}</td>
            <td><b>Placa:</b> <span className="font-mono font-bold">{t.placa_cavalo || "—"}</span>{t.placa_carreta && <> / <span className="font-mono">{t.placa_carreta}</span></>}{t.frota && <> · <b>Frota</b> {t.frota}</>}</td>
          </tr>
          <tr>
            <td className="py-1"><b>Chegada:</b> {fmtDT(t.chegou_em)}</td>
            <td><b>Entrada:</b> {fmtDT(t.entrou_em)}</td>
            <td><b>1ª Pesagem:</b> {fmtDT(t.peso1_em)}</td>
            <td><b>2ª Pesagem:</b> {fmtDT(t.peso2_em)}{t.saiu_em && <> · <b>Saída:</b> {fmtDT(t.saiu_em)}</>}</td>
          </tr>
          <tr>
            <td className="py-1"><b>Operação:</b> {TIPO_TXT[t.tipo_operacao] || t.tipo_operacao}{fins.length > 0 && <> — {fins.map((f) => FINALIDADES[f]).join(" + ")}</>}</td>
            <td><b>Destino:</b> {t.destino || "—"}</td>
            <td><b>Nota Fiscal:</b> <span className="font-mono">{t.nota_fiscal || "—"}</span></td>
            <td><b>Produto:</b> {t.produto || "—"}</td>
          </tr>
        </tbody>
      </table>

      {/* Pesagem oficial */}
      <div className="mt-4 border-2 border-black">
        <div className="bg-black text-white px-3 py-1 font-bold uppercase text-[11px]">Pesagem Oficial</div>
        <table className="w-full text-center">
          <thead>
            <tr className="border-b border-black text-[10px] uppercase">
              <th className="py-2">{r1}</th>
              <th>{r2}</th>
              <th>{r3}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="font-mono text-xl font-black">
              <td className="py-3" data-testid="ticket-peso1">{kg(t.peso1)}</td>
              <td data-testid="ticket-peso2">{kg(t.peso2)}</td>
              <td className="bg-gray-100" data-testid="ticket-liquido">{kg(t.peso_liquido)}</td>
            </tr>
            <tr className="text-[10px] text-gray-700">
              <td className="pb-2">{t.peso1_em ? `${fmtDT(t.peso1_em)} · ${t.peso1_origem === "SERIAL" ? "leitura do indicador" : "digitado"} · ${t.peso1_por}` : "—"}</td>
              <td className="pb-2">{t.peso2_em ? `${fmtDT(t.peso2_em)} · ${t.peso2_origem === "SERIAL" ? "leitura do indicador" : "digitado"} · ${t.peso2_por}` : "pendente"}</td>
              <td className="pb-2 bg-gray-100">{t.peso_liquido != null ? "|2ª − 1ª|" : "—"}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Resumo da operação */}
      <div className="mt-4 font-bold uppercase text-[11px]">Resumo da operação</div>
      <table className="w-full mt-1 border-collapse text-[10px]">
        <thead>
          <tr className="border-y-2 border-black text-left uppercase">
            <th className="py-1 pr-2">#</th>
            <th className="pr-2">Finalidade</th>
            <th className="pr-2">Status</th>
            <th className="pr-2">Destino</th>
            <th className="pr-2">NF</th>
            <th>Produto</th>
          </tr>
        </thead>
        <tbody>
          {fins.length === 0 && (
            <tr className="border-b border-gray-400">
              <td className="py-1 pr-2">1</td>
              <td className="pr-2 font-bold">{TIPO_TXT[t.tipo_operacao] || t.tipo_operacao}</td>
              <td className="pr-2 font-bold">{t.status === "FINALIZADO" ? "FEITO" : "EM ANDAMENTO"}</td>
              <td className="pr-2">—</td>
              <td className="pr-2 font-mono">{t.nota_fiscal || "—"}</td>
              <td>{t.produto || "—"}</td>
            </tr>
          )}
          {fins.map((f, i) => (
            <tr key={f} className="border-b border-gray-400">
              <td className="py-1 pr-2">{i + 1}</td>
              <td className="pr-2 font-bold">{FINALIDADES[f]}</td>
              <td className="pr-2 font-bold">{ETAPA_TXT[t.etapas?.[f]] || "—"}</td>
              <td className="pr-2">{f === "CARREGAR_LOAD" ? (t.destino || "—") : "—"}</td>
              <td className="pr-2 font-mono">{t.nota_fiscal || "—"}</td>
              <td>{t.produto || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {t.observacoes && <div className="mt-2 text-[11px]"><b>Observações:</b> {t.observacoes}</div>}

      {/* Assinaturas */}
      <div className="mt-12 grid grid-cols-2 gap-16">
        <div className="border-t border-black pt-1 text-center text-[10px] uppercase">Operador da Balança</div>
        <div className="border-t border-black pt-1 text-center text-[10px] uppercase">Motorista — {t.nome}</div>
      </div>
    </div>
  );
}
